import { Box, AccordionProps } from "@mui/material";
import { useState } from "react";
import Flex from "../../Flex";
import Text from "../../Text";
import { STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT } from "../../../constants";
import { formatDate } from "../../../utils/date-time-utility";
import { DEFAULT_FONTSIZE } from "../../../utils/utils";
import CustomAccordion from "../../CustomAccordion";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useDisclosure from "../../../hooks/use-disclosure";
import AllFilePreview from "../../ViewAllFile";
import { DOC_TYPE_MAPPER } from "../../../pages/app/provider/patient/patient-list/patient-chart-new/document/document-constant";

interface License {
  uuid: string;
  licenseState?: string;
  licenseNumber?: string;
  expiryDate?: string;
  licenseActiveDate?: string;
  licenseDocument?: any;
  licenseDocumentFormat?: string;
  licenseName?: string;
}

interface Dea {
  uuid: string;
  deaState?: string;
  deaNumber?: string;
  deaTermDate?: string;
  deaActiveDate?: string;
}

const LicensedAndDeaInformation = ({
  licenceInformation = [],
  deaInformation = [],
}: {
  licenceInformation: License[];
  deaInformation: Dea[];
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState<boolean[]>([
    true,
    true,
  ]);

  const handleAccordionChange = (index: number, isExpanded: boolean) => {
    setExpandedAccordions((prev) =>
      prev.map((expanded, i) => (i === index ? isExpanded : expanded))
    );
  };

  const previewFormDisclosure = useDisclosure(false);
  return (
    // file={"https://ecarehealth-api.s3.ap-south-1.amazonaws.com/qa/d10ea365-1758-4b67-aafd-7b8766315975/documents/76f6a031-ee74-4126-8c1f-4941cbee91f3.pdf?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20260128T132041Z&X-Amz-SignedHeaders=host&X-Amz-Credential=AKIAXPMUPPQDUKS5GGF5%2F20260128%2Fap-south-1%2Fs3%2Faws4_request&X-Amz-Expires=86400&X-Amz-Signature=47f1910f5f790efd54344c233930a65becf02cc47ccd6f33ad1bb1d561c4beac"}
    // fileType={"pdf"}
    <>
      <AllFilePreview
        disclosureHost={previewFormDisclosure}
        file={previewFormDisclosure.data?.document}
        fileType={
          DOC_TYPE_MAPPER[previewFormDisclosure.data?.documentFormat] ?? "other"
        }
        title="Preview Document"
        w="60vw"
      />

      {licenceInformation?.length > 0 && (
        <CustomAccordion
          expanded={expandedAccordions[0]}
          accordionProps={
            {
              onChange: (_, isExpanded: boolean) =>
                handleAccordionChange(0, isExpanded),
            } as AccordionProps
          }
          accordionSummeryProps={{
            style: { padding: 2 },
          }}
          accordionSummaryContent={
            <Text fontWeight={450} ml={2} color="black">
              Licensed Information
            </Text>
          }
          accordionDetailsContent={
            <Flex flexDirection="column" gap="10px" ml={2}>
              {licenceInformation.map((info, i) => (
                <Box key={info.uuid || i} mt={1}>
                  <Flex gap={"22px"} flexWrap="wrap">
                    <Text display="inline" fontWeight="medium">
                      {i + 1}.
                    </Text>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        License Name :{" "}
                      </Text>
                      <Text display="inline">{info.licenseName || ""}</Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        State :{" "}
                      </Text>
                      <Text display="inline">
                        {STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[
                          info.licenseState as any
                        ] || "-"}
                      </Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        Number :{" "}
                      </Text>
                      <Text display="inline">{info.licenseNumber || "-"}</Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        Active Date :{" "}
                      </Text>
                      <Text display="inline">
                        {info.licenseActiveDate
                          ? formatDate(new Date(info.licenseActiveDate))
                          : "-"}
                      </Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        Expiry Date :{" "}
                      </Text>
                      <Text display="inline">
                        {info.expiryDate
                          ? formatDate(new Date(info.expiryDate))
                          : "-"}
                      </Text>
                      <VisibilityIcon
                        style={{
                          display: "inline",
                          marginLeft: "20px",
                          cursor: info.licenseDocument
                            ? "pointer"
                            : "not-allowed",
                        }}
                        fontSize="small"
                        color={info.licenseDocument ? "primary" : "disabled"}
                        onClick={() => {
                          if (info.licenseDocument) {
                            previewFormDisclosure.open({
                              document: info.licenseDocument,
                              documentFormat:
                                info.licenseDocumentFormat ?? undefined,
                            });
                          }
                        }}
                      />
                    </Box>
                  </Flex>
                </Box>
              ))}
            </Flex>
          }
        />
      )}
      {deaInformation?.length > 0 && (
        <CustomAccordion
          expanded={expandedAccordions[1]}
          accordionProps={
            {
              onChange: (_, isExpanded) => handleAccordionChange(1, isExpanded),
            } as AccordionProps
          }
          accordionSummeryProps={{
            style: { padding: 2 },
          }}
          accordionSummaryContent={
            <Text fontWeight={450} ml={2} color="black">
              DEA Information
            </Text>
          }
          accordionDetailsContent={
            <Flex flexDirection="column" gap="10px" ml={2}>
              {deaInformation.map((dea, i) => (
                <Box key={dea.uuid || i} p={0.2}>
                  <Flex gap={"22px"} flexWrap="wrap">
                    <Text display="inline" fontWeight="medium">
                      {i + 1}.
                    </Text>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        State:{" "}
                      </Text>
                      <Text display="inline">
                        {STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[
                          dea.deaState as any
                        ] || "-"}
                      </Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        DEA Number:{" "}
                      </Text>
                      <Text display="inline">{dea.deaNumber || "-"}</Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        Active Date:{" "}
                      </Text>
                      <Text display="inline">
                        {dea.deaActiveDate
                          ? formatDate(new Date(dea.deaActiveDate))
                          : "-"}
                      </Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text
                        color="#1A1A1A80"
                        fontWeight="medium"
                        fontSize={DEFAULT_FONTSIZE.fontSize}
                        display="inline"
                      >
                        Expiry Date:{" "}
                      </Text>
                      <Text display="inline">
                        {dea.deaTermDate
                          ? formatDate(new Date(dea.deaTermDate))
                          : "-"}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </Flex>
          }
        />
      )}
    </>
  );
};

export default LicensedAndDeaInformation;
